<script lang="ts">
  import type { SurfaceSnapshot, SurfaceProject, SurfaceTask } from "../lib/types";
  import { relativeDate } from "../lib/format";
  import { completeTask, pinToToday, unpinFromToday } from "../lib/api";
  import { loadSnapshot } from "../lib/store";
  import { optimisticDone, markDone, clearDone, reconcile as reconcileDone } from "../lib/optimistic";
  import { pinnedToday, markPinned, markUnpinned } from "../lib/pinned";
  import StarButton from "../lib/StarButton.svelte";
  import SourceBadges from "../lib/SourceBadges.svelte";
  import FilterChips from "../lib/FilterChips.svelte";
  import { applyFilter, loadFilter, saveFilter, type Filter } from "../lib/filter";

  let { data }: { data: SurfaceSnapshot } = $props();

  let pending = $state<Set<string>>(new Set());
  let lastError = $state<string | null>(null);
  let filter = $state<Filter>(loadFilter("surface:filter:today"));
  let softCapDismissed = $state(false);
  let eodDismissed = $state(false);

  function tomorrowISO(): string {
    const today = data.vault_today;
    const [y, m, d] = today.split("-").map(Number);
    return new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10);
  }

  function setFilter(f: Filter) {
    filter = f;
    saveFilter("surface:filter:today", f);
  }

  $effect(() => {
    const confirmed = data.tasks.filter((t) => t.state === "done").map((t) => t.fingerprint);
    if (confirmed.length > 0) reconcileDone(confirmed);
  });

  function projectBySlug(slug: string | null): SurfaceProject | undefined {
    if (!slug) return undefined;
    return data.projects.find((p) => p.slug === slug);
  }

  function rank(p: SurfaceProject): number {
    return p.priority === "high" ? 0 : p.priority === "medium" ? 1 : 2;
  }

  function isOpen(t: SurfaceTask, opt: Record<string, unknown>): boolean {
    return t.state === "open" && !(t.fingerprint in opt);
  }

  function isPinnedAny(t: SurfaceTask): boolean {
    const today = data.vault_today;
    const local = $pinnedToday[t.fingerprint];
    if (local) return local.day === today;
    return t.pinned_today === today;
  }
  function isEveningPinned(t: SurfaceTask): boolean {
    if (!isPinnedAny(t)) return false;
    const local = $pinnedToday[t.fingerprint];
    if (local) return !!local.evening;
    return !!t.pinned_evening;
  }
  function pinTs(fp: string): number {
    const e = $pinnedToday[fp];
    return e ? e.ts : 0;
  }

  // ---- derived task lists ----------------------------------------------

  const pinnedToday_ = $derived(
    data.tasks
      .filter((t) => isOpen(t, $optimisticDone))
      .filter((t) => isPinnedAny(t) && !isEveningPinned(t))
      .filter((t) => applyFilter(t, filter))
      .sort((a, b) => pinTs(b.fingerprint) - pinTs(a.fingerprint)),
  );

  const pinnedEvening = $derived(
    data.tasks
      .filter((t) => isOpen(t, $optimisticDone))
      .filter((t) => isEveningPinned(t))
      .filter((t) => applyFilter(t, filter))
      .sort((a, b) => pinTs(b.fingerprint) - pinTs(a.fingerprint)),
  );

  const totalPinned = $derived(
    data.tasks
      .filter((t) => isOpen(t, $optimisticDone))
      .filter((t) => isPinnedAny(t)).length,
  );

  function topActions(opt: Record<string, unknown>): SurfaceTask[] {
    const projects = [...data.projects]
      .filter((p) => p.status === "active")
      .sort((a, b) => rank(a) - rank(b) || a.title.localeCompare(b.title));
    const out: SurfaceTask[] = [];
    const used = new Set<string>();
    for (const p of projects) {
      if (out.length >= 5) break;
      const na = data.tasks.find(
        (t) =>
          t.project_slug === p.slug &&
          t.is_next_action &&
          t.state === "open" &&
          !(t.fingerprint in opt) &&
          !used.has(t.fingerprint),
      );
      if (na) {
        out.push(na);
        used.add(na.fingerprint);
        if (out.length >= 5) break;
      }
      const others = data.tasks.filter(
        (t) =>
          t.project_slug === p.slug &&
          t.state === "open" &&
          !t.is_next_action &&
          !(t.fingerprint in opt) &&
          !used.has(t.fingerprint),
      );
      for (const t of others.slice(0, 2)) {
        out.push(t);
        used.add(t.fingerprint);
        if (out.length >= 5) break;
      }
    }
    return out;
  }

  const suggested = $derived(
    pinnedToday_.length >= 3
      ? []
      : topActions($optimisticDone)
          .filter((t) => !isPinnedAny(t))
          .filter((t) => applyFilter(t, filter)),
  );

  function dueToday(): SurfaceProject[] {
    return data.projects.filter((p) => p.due === data.vault_today);
  }
  function overdue(): SurfaceProject[] {
    const today = data.vault_today;
    return data.projects
      .filter((p) => p.due && p.due < today && p.status === "active")
      .sort((a, b) => (a.due ?? "").localeCompare(b.due ?? ""));
  }
  const due = $derived(dueToday());
  const over = $derived(overdue());

  const isEvening = $derived.by(() => {
    const h = new Date().getHours();
    return h >= 18;
  });
  const showEod = $derived(
    isEvening && !eodDismissed && (pinnedToday_.length + pinnedEvening.length) > 0,
  );

  const showSoftCap = $derived(totalPinned >= 8 && !softCapDismissed);

  function reportError(msg: string) {
    lastError = msg;
  }

  async function toggle(t: SurfaceTask) {
    if (pending.has(t.fingerprint)) return;
    lastError = null;
    pending = new Set([...pending, t.fingerprint]);
    markDone(t.fingerprint);
    try {
      await completeTask({
        file: t.file,
        line: t.line,
        text: t.text,
        gt_account: t.account,
        gt_id: t.gt_id,
        target_state: "done",
      });
      void loadSnapshot({ fresh: true });
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      lastError = `Couldn't complete: ${msg}`;
      clearDone(t.fingerprint);
    } finally {
      pending = new Set([...pending].filter((fp) => fp !== t.fingerprint));
    }
  }

  function dismissTomorrow(_fp: string) {}

  async function rollAllToTomorrow() {
    const targets = [...pinnedToday_, ...pinnedEvening];
    eodDismissed = true;
    const tomorrow = tomorrowISO();
    for (const t of targets) {
      const evening = !!$pinnedToday[t.fingerprint]?.evening;
      markPinned(t.fingerprint, evening, tomorrow);
      try {
        await pinToToday({ fingerprint: t.fingerprint, evening, day: tomorrow });
      } catch (e) {
        lastError = `Reschedule failed: ${e instanceof Error ? e.message : String(e)}`;
      }
    }
  }

  async function dropAll() {
    const targets = [...pinnedToday_, ...pinnedEvening];
    eodDismissed = true;
    for (const t of targets) {
      markUnpinned(t.fingerprint);
      try {
        await unpinFromToday({ fingerprint: t.fingerprint });
      } catch (e) {
        lastError = `Drop failed: ${e instanceof Error ? e.message : String(e)}`;
      }
    }
  }

  function leaveEod() {
    eodDismissed = true;
  }
</script>

<div class="page">
  <header class="hero">
    <h1>Today</h1>
    <p class="subtitle">
      {pinnedToday_.length + pinnedEvening.length} pinned{suggested.length ? ` · ${suggested.length} suggested` : ""}{over.length ? ` · ${over.length} overdue` : ""}
    </p>
  </header>

  <FilterChips value={filter} onchange={setFilter} />

  {#if showSoftCap}
    <div class="banner softcap">
      <div>
        <strong>You've got {totalPinned} items on Today</strong> — that's a lot. Consider unstarring some.
      </div>
      <button class="link-btn" onclick={() => (softCapDismissed = true)}>Dismiss</button>
    </div>
  {/if}

  {#if lastError}
    <div class="banner error">{lastError}</div>
  {/if}

  {#if pinnedToday_.length > 0}
    <section>
      <h2 class="section-title">Pinned</h2>
      <ul class="grouped-list">
        {#each pinnedToday_ as t (t.fingerprint)}
          {@const p = projectBySlug(t.project_slug)}
          <li class:pending={pending.has(t.fingerprint)}>
            <button
              class="check"
              type="button"
              aria-label="Mark done"
              disabled={pending.has(t.fingerprint)}
              onclick={() => toggle(t)}
            >
              <span class="checkbox"></span>
            </button>
            <div class="body">
              <div class="row-top">
                <span class="project-tag">
                  {#if p?.priority === "high"}<span class="dot priority-high"></span>{/if}
                  {#if p?.priority === "medium"}<span class="dot priority-medium"></span>{/if}
                  {p?.title ?? (t.fingerprint.startsWith("gt:") ? "Google Tasks" : "(no project)")}
                </span>
                {#if t.is_next_action}<span class="pill">next</span>{/if}
              </div>
              <div class="task-text">{t.text}</div>
              <div class="row-meta">
                {#if p?.domain}<span class="dot {p.domain}"></span><span class="faint">{p.domain}</span>{/if}
                {#if p?.due}<span class="faint"> · due {relativeDate(p.due)}</span>{/if}
                {#if t.section && !t.is_next_action}<span class="faint"> · {t.section}</span>{/if}
              </div>
            </div>
            <div class="row-end">
              <SourceBadges task={t} />
              <StarButton task={t} onerror={reportError} ondismiss={dismissTomorrow} />
            </div>
          </li>
        {/each}
      </ul>
    </section>
  {/if}

  {#if suggested.length > 0}
    <section>
      <h2 class="section-title">Suggested</h2>
      <ul class="grouped-list muted-list">
        {#each suggested as t (t.fingerprint)}
          {@const p = projectBySlug(t.project_slug)}
          <li class:pending={pending.has(t.fingerprint)}>
            <button
              class="check"
              type="button"
              aria-label="Mark done"
              disabled={pending.has(t.fingerprint)}
              onclick={() => toggle(t)}
            >
              <span class="checkbox"></span>
            </button>
            <div class="body">
              <div class="row-top">
                <span class="project-tag">
                  {#if p?.priority === "high"}<span class="dot priority-high"></span>{/if}
                  {#if p?.priority === "medium"}<span class="dot priority-medium"></span>{/if}
                  {p?.title ?? "(no project)"}
                </span>
                {#if t.is_next_action}<span class="pill">next</span>{/if}
              </div>
              <div class="task-text">{t.text}</div>
              <div class="row-meta">
                {#if p?.domain}<span class="dot {p.domain}"></span><span class="faint">{p.domain}</span>{/if}
                {#if p?.due}<span class="faint"> · due {relativeDate(p.due)}</span>{/if}
                {#if t.section && !t.is_next_action}<span class="faint"> · {t.section}</span>{/if}
              </div>
            </div>
            <div class="row-end">
              <SourceBadges task={t} />
              <StarButton task={t} onerror={reportError} ondismiss={dismissTomorrow} size="sm" />
            </div>
          </li>
        {/each}
      </ul>
    </section>
  {/if}

  {#if pinnedToday_.length === 0 && suggested.length === 0}
    <p class="empty">Nothing pinned. Tap a star on any task to bring it here, or check Projects/Inbox.</p>
  {/if}

  {#if due.length > 0}
    <section>
      <h2 class="section-title">Due Today</h2>
      <ul class="grouped-list simple">
        {#each due as p (p.slug)}
          <li class="simple-row"><strong>{p.title}</strong><span class="faint">{p.open_count} open</span></li>
        {/each}
      </ul>
    </section>
  {/if}

  {#if over.length > 0}
    <section>
      <h2 class="section-title">Overdue Projects</h2>
      <ul class="grouped-list simple">
        {#each over as p (p.slug)}
          <li class="simple-row"><strong>{p.title}</strong><span class="faint">was due {relativeDate(p.due)}</span></li>
        {/each}
      </ul>
    </section>
  {/if}

  {#if pinnedEvening.length > 0}
    <section>
      <h2 class="section-title">This Evening</h2>
      <ul class="grouped-list">
        {#each pinnedEvening as t (t.fingerprint)}
          {@const p = projectBySlug(t.project_slug)}
          <li class:pending={pending.has(t.fingerprint)}>
            <button
              class="check"
              type="button"
              aria-label="Mark done"
              disabled={pending.has(t.fingerprint)}
              onclick={() => toggle(t)}
            >
              <span class="checkbox"></span>
            </button>
            <div class="body">
              <div class="row-top">
                <span class="project-tag">
                  {p?.title ?? (t.fingerprint.startsWith("gt:") ? "Google Tasks" : "(no project)")}
                </span>
              </div>
              <div class="task-text">{t.text}</div>
            </div>
            <div class="row-end">
              <SourceBadges task={t} />
              <StarButton task={t} onerror={reportError} ondismiss={dismissTomorrow} />
            </div>
          </li>
        {/each}
      </ul>
    </section>
  {/if}

  {#if showEod}
    <div class="eod">
      <p class="eod-text">{pinnedToday_.length + pinnedEvening.length} unfinished today.</p>
      <div class="eod-actions">
        <button class="primary-sm" onclick={rollAllToTomorrow}>Roll to tomorrow</button>
        <button class="link-btn" onclick={dropAll}>Drop</button>
        <button class="link-btn muted-link" onclick={leaveEod}>Leave</button>
      </div>
    </div>
  {/if}
</div>

<style>
  .page { padding: 8px 16px 140px; max-width: 720px; margin: 0 auto; }

  /* Large Title — iOS Navigation Large Title pattern */
  .hero { padding: 12px 4px 4px; }
  h1 {
    font-size: 34px;
    line-height: 1.05;
    margin: 0;
    font-weight: 700;
    letter-spacing: -0.025em;
  }
  .subtitle {
    color: var(--fg-dim);
    font-size: 15px;
    margin: 4px 0 12px;
    letter-spacing: -0.005em;
  }

  /* Section header — iOS grouped list footnote style */
  .section-title {
    font-size: 13px;
    margin: 22px 16px 6px;
    color: var(--fg-dim);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-weight: 500;
  }

  section { margin-bottom: 4px; }
  .empty { color: var(--fg-dim); padding: 20px 4px; font-size: 15px; }

  /* Banners — iOS notice strip inside content */
  .banner.error {
    background: rgba(255, 69, 58, 0.14);
    color: #ff8a82;
    border: 1px solid rgba(255, 69, 58, 0.28);
    padding: 10px 14px;
    border-radius: 12px;
    font-size: 14px;
    margin: 4px 0 12px;
  }
  .banner.softcap {
    background: rgba(255, 214, 10, 0.10);
    color: #ffe066;
    border: 1px solid rgba(255, 214, 10, 0.22);
    padding: 12px 14px;
    border-radius: 12px;
    font-size: 14px;
    margin: 4px 0 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
  }

  .link-btn {
    color: var(--accent);
    padding: 6px 10px;
    font-size: 14px;
    font-weight: 500;
    border-radius: 8px;
  }
  .link-btn:active { background: var(--tap); }
  .link-btn.muted-link { color: var(--fg-dim); }

  .primary-sm {
    background: var(--accent);
    color: var(--accent-fg);
    padding: 8px 16px;
    border-radius: 980px;
    font-weight: 600;
    font-size: 14px;
    min-height: 32px;
  }
  .primary-sm:active { background: var(--accent-pressed); }

  /* Inset-grouped list — single rounded container, internal hairlines */
  .grouped-list {
    list-style: none;
    padding: 0;
    margin: 0;
    background: var(--bg-elev);
    border-radius: 12px;
    overflow: hidden;
  }
  .grouped-list.muted-list { background: rgba(28, 28, 30, 0.55); }
  .grouped-list li {
    display: flex;
    gap: 4px;
    align-items: flex-start;
    padding: 12px 12px 12px 4px;
    transition: opacity 0.2s, background 0.15s;
    border-bottom: 0.5px solid var(--separator);
  }
  .grouped-list li:last-child { border-bottom: 0; }
  .grouped-list li:active { background: var(--tap); }
  .grouped-list li.pending { opacity: 0.5; }

  /* Simple grouped (project rows in due/overdue) */
  .grouped-list.simple .simple-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 14px;
  }
  .grouped-list.simple strong { font-weight: 500; font-size: 16px; }

  /* Checkbox row */
  .check { flex-shrink: 0; padding: 6px 10px; border-radius: 8px; }
  .check:active .checkbox { border-color: var(--accent); }
  .check:disabled { cursor: default; }
  .checkbox {
    display: block;
    width: 22px;
    height: 22px;
    border: 1.5px solid var(--fg-faint);
    border-radius: 50%;
    transition: border-color 0.15s, background 0.15s;
  }

  .body { flex: 1; min-width: 0; }
  .row-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    margin-bottom: 2px;
  }
  .project-tag {
    font-size: 13px;
    color: var(--fg-dim);
    font-weight: 500;
  }
  .task-text {
    font-size: 16px;
    line-height: 1.32;
    margin-bottom: 4px;
    color: var(--fg);
  }
  .row-meta { font-size: 12px; color: var(--fg-dim); }
  .row-end { display: flex; align-items: center; gap: 6px; flex-shrink: 0; padding-top: 4px; }

  /* End-of-day card */
  .eod {
    margin-top: 28px;
    padding: 16px;
    background: var(--bg-elev);
    border-radius: 14px;
  }
  .eod-text { margin: 0 0 12px; color: var(--fg-dim); font-size: 14px; }
  .eod-actions { display: flex; gap: 4px; align-items: center; flex-wrap: wrap; }
</style>
